/**
 * "The variant's current EAN" for display and scan matching.
 *
 * Identity lives in `variant.barcodes` (VariantBarcode, active rows, primary
 * first — the backend includes it on product, GRN and PO payloads). The
 * pricing history (`VariantPricing.eanCode`, one value per price generation)
 * is the fallback for payloads that don't carry barcodes and for variants
 * whose codes predate the barcode table: the newest price row that carries
 * one, active rows first, so a retired generation never shadows the one on
 * the shelf.
 */
export interface EanPricingRowLike {
  eanCode?: string | null;
  isActive?: boolean | null;
  updatedAt?: string | Date | null;
  validFrom?: string | Date | null;
}

export interface BarcodeRowLike {
  code: string;
  isPrimary?: boolean | null;
  isActive?: boolean | null;
}

export interface VariantEanLike {
  pricing?: EanPricingRowLike[] | null;
  barcodes?: BarcodeRowLike[] | null;
}

const ts = (v: string | Date | null | undefined): number =>
  v ? new Date(v).getTime() : 0;

export function currentEanOf(variant: VariantEanLike | null | undefined): string {
  const active = (variant?.barcodes ?? []).filter(
    (b) => b?.code?.trim() && b.isActive !== false,
  );
  if (active.length) {
    return (active.find((b) => b.isPrimary) ?? active[0]).code.trim();
  }

  const rows = (variant?.pricing ?? []).filter((r) => r?.eanCode?.trim());
  if (!rows.length) return '';
  rows.sort((a, b) => {
    const activeDiff = Number(!!b.isActive) - Number(!!a.isActive);
    if (activeDiff) return activeDiff;
    return ts(b.updatedAt ?? b.validFrom) - ts(a.updatedAt ?? a.validFrom);
  });
  return rows[0].eanCode!.trim();
}

/** Every code the variant is known by: active barcodes, then pricing-history EANs. */
export function allCodesOf(variant: VariantEanLike | null | undefined): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (c: string | null | undefined) => {
    const t = c?.trim();
    if (t && !seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
  };
  for (const b of variant?.barcodes ?? []) if (b?.isActive !== false) push(b?.code);
  for (const p of variant?.pricing ?? []) push(p?.eanCode);
  return out;
}

/**
 * Decorate "Search by EAN" results for the document forms (TO / GRN / PO).
 * `matchedEan` is the code the typed term hit — identity barcode or a price
 * generation's EAN — falling back to the variant's current EAN so the option
 * always shows a code. The backend already moved the matching price row to
 * `pricing[0]`, so the pick defaults cost / MRP to that pack.
 */
export function withMatchedEan<T extends { variants?: VariantEanLike[] | null }>(
  items: T[],
  term: string,
): (T & { matchedEan: string; ean: string })[] {
  const t = (term ?? '').trim().toLowerCase();
  return items.map((item) => {
    const variant = item.variants?.[0];
    const matchedEan =
      allCodesOf(variant).find((c) => c.toLowerCase().includes(t)) ||
      currentEanOf(variant);
    return { ...item, matchedEan, ean: matchedEan };
  });
}

/** Does this variant carry the scanned code (either surface), case-insensitively? */
export function variantHasCode(
  variant: VariantEanLike | null | undefined,
  code: string | null | undefined,
): boolean {
  const c = code?.trim().toLowerCase();
  if (!c) return false;
  return allCodesOf(variant).some((x) => x.toLowerCase() === c);
}
